import { z } from 'zod'

import { pagedSchema } from '../core'

const text = z.string().nullish()

const flag = (value: unknown): boolean => value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true')

/**
 * How far a project's reviews have come: a map of flags, read loosely (a flag may be a boolean or the text "true"; a missing one means not
 * reviewed). The workflow the project follows is its own field (`workflowType`); older answers that still carried it in here are read too.
 */
export const workflowProgressSchema = z.record(z.string(), z.unknown()).transform((value) => ({
  workflowType: typeof value.workflowType === 'string' ? value.workflowType : typeof value.workflowtype === 'string' ? value.workflowtype : null,
  assetReview: flag(value.assetReview),
  artContent: flag(value.artContent),
  creativeBrief: flag(value.creativeBrief),
}))

/** What is sent for it: the review flags (every update sends all of them back, so a change to the project never undoes a review). */
export interface WorkflowProgressBody {
  assetReview?: boolean
  artContent?: boolean
  creativeBrief?: boolean
}

/** A project as the backend sends it. Its `accountId` is the account of whoever created it; `assignedAccountId` is the client it is for. */
export const projectSchema = z.object({
  id: z.string(),
  projectName: text,
  accountId: z.string(),
  /** The CLIENT account the project is for (the creator's own is `accountId`). Projects made before it existed have none: the client was `accountId`. */
  assignedAccountId: text,
  createdBy: z.string(),
  instructions: text,
  outputFormats: z.array(z.string()).nullish(),
  artworkSize: text,
  status: text,
  createdAt: z.string(),
  updatedAt: text,
  /** The latest generated version's number, and the storage paths of its pictures (one per page). Both are empty until a design has been made. */
  generatedVersionNumber: z.number().nullish(),
  generatedImageUrls: z.array(z.string()).nullish(),
  /** Which workflow the project follows: `generate_template`, `indesign_template` or `existing_template`. */
  workflowType: text,
  workflowProgress: workflowProgressSchema.nullish(),
  /** The project's SKU ID, set when its design is approved. */
  sku: text,
  /** Set when saving the project starts background work (a changed brief is read again): follow it with the sse-tasks API. */
  taskId: text,
})
export type ProjectDto = z.infer<typeof projectSchema>

export const projectPageSchema = pagedSchema(projectSchema)

/** POST /api/v1/projects. `accountId` is the account of the person creating the project and `assignedAccountId` the CLIENT account it is for (nothing is mapped at this point: the client's people are put on the project when the design is sent for review); `instructions` cannot be empty; `outputFormats` are from PDF, PNG, JPG, IDML; `artworkSize` is "8.5x11" (inches). */
export interface ProjectCreateBody {
  projectName: string
  accountId: string
  assignedAccountId: string
  instructions: string
  outputFormats: string[]
  artworkSize: string
  createdBy: string
  /** Which workflow the project follows. */
  workflowType?: string
  workflowProgress?: WorkflowProgressBody
}

/** PUT /api/v1/projects/{id}: the same fields without `createdBy`, plus the status (the backend wants it back with every update). */
export interface ProjectUpdateBody {
  projectName: string
  accountId: string
  assignedAccountId?: string
  instructions: string
  outputFormats: string[]
  artworkSize: string
  status: string
  /** The SKU ID. Sent back with every update once there is one, so a change to the project never clears it. */
  sku?: string
  workflowType?: string
  /** Sent back whole with every update, so a change to the project never undoes a review. */
  workflowProgress?: WorkflowProgressBody
}

export interface ProjectFilters {
  accountId?: string
  createdBy?: string
  status?: string
  search?: string
}
